import { useCallback } from 'react';
import { useHydrateAssetsInWindow } from '@frameio/assets/hydration';
import { AssetContextProvider } from '@frameio/assets/shared';
import { useUploads } from '@frameio/transfers/hooks';
import { useGetAdaptiveSpacingNumber, WindowedTreeView } from '@frameio/vapor';
import {
  AllCollections,
  C2CConnections,
  Collection,
  Folder,
  type FolderDndNamespace,
  FolderDropZone,
  RootFolder,
  SubTreeContainer,
  Trash,
  TreeChildrenWrapper,
  UploadLinks,
} from './components';
import {
  ProjectTreeContextProvider,
  type ProjectTreeContextValue,
  ProjectTreeUploadsContextProvider,
} from './contexts';
import type { useProjectFolders } from './hooks';
import type {
  ProjectItemProps,
  ProjectTreeItemType,
  WindowedProjectTreeProps,
} from './types';

export type ProjectTreeProps = {
  dndNamespace: FolderDndNamespace | null;
  enableTopShadow?: boolean;
  onBoundarySeen: (type: ProjectTreeItemType, parentId?: string) => void;
  paddingX?: number;
  paddingY?: number;
  rootFolderId: string;
} & Pick<ReturnType<typeof useProjectFolders>, 'tree'> &
  Pick<
    ProjectTreeContextValue,
    | 'disableContextMenus'
    | 'disableOpenOnDesktop'
    | 'enableHiddenLinks'
    | 'onItemSelect'
    | 'overrideRootFolderName'
    | 'selectedItem'
    | 'toggleItem'
  >;

export function ProjectTree({
  disableContextMenus,
  disableOpenOnDesktop,
  dndNamespace,
  enableHiddenLinks = false,
  enableTopShadow = false,
  onItemSelect,
  onBoundarySeen,
  overrideRootFolderName,
  paddingX,
  paddingY,
  rootFolderId,
  selectedItem,
  toggleItem,
  tree,
}: ProjectTreeProps) {
  const hydrateAssetsInWindow = useHydrateAssetsInWindow();
  const defaultItemHeight = useGetAdaptiveSpacingNumber({
    spacing: ITEM_HEIGHT,
  });
  const adaptivePaddingX = useGetAdaptiveSpacingNumber({
    spacing: paddingX ?? PADDING_X,
  });
  const adaptivePaddingY = useGetAdaptiveSpacingNumber({
    spacing: paddingY ?? PADDING_Y,
  });

  const handleVisibleItemsChanged = useCallback<
    NonNullable<WindowedProjectTreeProps['onVisibleItemsChanged']>
  >(
    (items) => {
      const boundaryItem = items.find(
        (item) =>
          item.props &&
          (item.props.type === 'folder' || item.props.type === 'collection') &&
          item.props.isBoundary
      );

      if (boundaryItem?.props) {
        onBoundarySeen(
          boundaryItem.props.type,
          boundaryItem.props.type === 'folder'
            ? boundaryItem.props.parentId
            : undefined
        );
      }

      const assetIds = items
        .filter((item) => item.props?.type === 'folder')
        .map((item) => item.props?.id ?? '');

      hydrateAssetsInWindow({ assetIds });
    },
    [hydrateAssetsInWindow, onBoundarySeen]
  );

  const startAt = useCallback<NonNullable<WindowedProjectTreeProps['startAt']>>(
    (props) =>
      props.type === selectedItem?.type && props.id === selectedItem.id,
    [selectedItem]
  );

  const itemComponent = useCallback<
    NonNullable<WindowedProjectTreeProps['itemComponent']>
  >(
    (props) => {
      const { id } = props;

      switch (props.type) {
        case 'hidden-root':
          return null;
        case 'trash':
          return <Trash key={id} {...props} />;
        case 'all-collections':
          return <AllCollections key={id} {...props} />;
        case 'collection':
          return <Collection key={id} {...props} />;
        case 'c2c-connections':
          return <C2CConnections key={id} {...props} />;
        case 'upload-links':
          return <UploadLinks key={id} {...props} />;
        case 'folder':
        case 'root-folder':
          if (dndNamespace === null) {
            return (
              <AssetContextProvider key={id} assetType="folder" id={id}>
                {props.type === 'root-folder' ? (
                  <RootFolder key={id} {...props} />
                ) : (
                  <Folder key={id} {...props} />
                )}
              </AssetContextProvider>
            );
          }

          return (
            <AssetContextProvider key={id} assetType="folder" id={id}>
              <FolderDropZone key={id} id={id}>
                {props.type === 'root-folder' ? (
                  <RootFolder key={id} {...props} />
                ) : (
                  <Folder key={id} {...props} />
                )}
              </FolderDropZone>
            </AssetContextProvider>
          );
        default:
          return <></>;
      }
    },
    [dndNamespace]
  );

  const { transfersDnd } = useUploads(rootFolderId);

  return (
    <ProjectTreeUploadsContextProvider
      dndZone="project-nav"
      transfersDnd={transfersDnd}
    >
      <ProjectTreeContextProvider
        disableContextMenus={disableContextMenus}
        disableOpenOnDesktop={disableOpenOnDesktop}
        enableHiddenLinks={enableHiddenLinks}
        folderDndNamepace={dndNamespace}
        onItemSelect={onItemSelect}
        overrideRootFolderName={overrideRootFolderName}
        selectedItem={selectedItem}
        toggleItem={toggleItem}
      >
        <WindowedTreeView<ProjectItemProps>
          childrenWrapper={TreeChildrenWrapper}
          containerComponent={SubTreeContainer}
          defaultItemHeight={defaultItemHeight}
          enableSharedHover={false}
          enableTopBorder={false}
          enableTopShadow={enableTopShadow}
          itemComponent={itemComponent}
          onVisibleItemsChanged={handleVisibleItemsChanged}
          overscan={OVERSCAN}
          paddingX={adaptivePaddingX}
          paddingY={adaptivePaddingY}
          startAt={startAt}
          tree={tree}
        />
      </ProjectTreeContextProvider>
    </ProjectTreeUploadsContextProvider>
  );
}

const ITEM_HEIGHT = 32;
const OVERSCAN = 0.1;
const PADDING_X = 12;
const PADDING_Y = 2;
